// "n + noun" with the right grammatical number. Arabic agrees the noun with
// the count (CLDR plural categories): 1 شهر واحد · 2 شهرين · 3–10 أشهر ·
// 11–99 شهراً · 100+ شهر. The dual uses the oblique form, which is right
// after a preposition ("على شهرين") and is what people write in forms.

export type CountUnit =
  | 'month'
  | 'day'
  | 'receipt'
  | 'request'
  | 'student'
  | 'row'
  | 'problem'
  | 'school'
  | 'minute'
  | 'hour'
  | 'advance'
  | 'payslip'
  | 'salary'
  | 'payment'
  | 'class'
  | 'staff';

type ArForms = [one: string, two: string, few: string, many: string, other: string];

const AR: Record<CountUnit, ArForms> = {
  month: ['شهر واحد', 'شهرين', '{n} أشهر', '{n} شهراً', '{n} شهر'],
  day: ['يوم واحد', 'يومين', '{n} أيام', '{n} يوماً', '{n} يوم'],
  receipt: ['وصل واحد', 'وصلين', '{n} وصولات', '{n} وصلاً', '{n} وصل'],
  request: ['طلب واحد', 'طلبين', '{n} طلبات', '{n} طلباً', '{n} طلب'],
  student: ['طالب واحد', 'طالبين', '{n} طلاب', '{n} طالباً', '{n} طالب'],
  row: ['سطر واحد', 'سطرين', '{n} أسطر', '{n} سطراً', '{n} سطر'],
  problem: ['مشكلة واحدة', 'مشكلتين', '{n} مشكلات', '{n} مشكلة', '{n} مشكلة'],
  school: ['مدرسة واحدة', 'مدرستين', '{n} مدارس', '{n} مدرسة', '{n} مدرسة'],
  minute: ['دقيقة واحدة', 'دقيقتين', '{n} دقائق', '{n} دقيقة', '{n} دقيقة'],
  hour: ['ساعة واحدة', 'ساعتين', '{n} ساعات', '{n} ساعة', '{n} ساعة'],
  advance: ['سلفة واحدة', 'سلفتين', '{n} سُلف', '{n} سلفة', '{n} سلفة'],
  payslip: ['قسيمة واحدة', 'قسيمتين', '{n} قسائم', '{n} قسيمة', '{n} قسيمة'],
  salary: ['راتب واحد', 'راتبين', '{n} رواتب', '{n} راتباً', '{n} راتب'],
  payment: ['دفعة واحدة', 'دفعتين', '{n} دفعات', '{n} دفعة', '{n} دفعة'],
  class: ['شعبة واحدة', 'شعبتين', '{n} شعب', '{n} شعبة', '{n} شعبة'],
  staff: ['موظف واحد', 'موظفَين', '{n} موظفين', '{n} موظفاً', '{n} موظف'],
};

const EN: Record<CountUnit, [one: string, other: string]> = {
  month: ['month', 'months'],
  day: ['day', 'days'],
  receipt: ['receipt', 'receipts'],
  request: ['item', 'items'],
  student: ['student', 'students'],
  row: ['row', 'rows'],
  problem: ['problem', 'problems'],
  school: ['school', 'schools'],
  minute: ['minute', 'minutes'],
  hour: ['hour', 'hours'],
  advance: ['advance', 'advances'],
  payslip: ['payslip', 'payslips'],
  salary: ['salary', 'salaries'],
  payment: ['payment', 'payments'],
  class: ['class', 'classes'],
  staff: ['staff member', 'staff members'],
};

const rules = new Map<string, Intl.PluralRules>();
const plural = (lang: string, n: number) => {
  let r = rules.get(lang);
  if (!r) rules.set(lang, (r = new Intl.PluralRules(lang)));
  return r.select(n);
};

/** `formatted` is n already formatted in the school's digits. */
export function countText(lang: 'ar' | 'en', n: number, unit: CountUnit, formatted: string): string {
  if (lang === 'en') return `${formatted} ${EN[unit][plural('en', n) === 'one' ? 0 : 1]}`;
  const [one, two, few, many, other] = AR[unit];
  const form = { one, two, few, many, other, zero: other }[plural('ar', n)] ?? other;
  return form.replace('{n}', formatted);
}
